import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import OrderLookup from '../../components/OrderLookup';
import { API_BASE } from 'config';

const API_ROOT = String(API_BASE || '').replace(/\/$/, '');

const DEMO_MODES = {
  dtc_supplements: {
    label: 'DTC — Supplements',
    tenantId: 'ecom-dtc-demo',
    assistantType: 'ecom-dtc-supplements',
    namespace: 'ecom-dtc-supplements',
    systemText:
      'Ask about products, ingredients, policies, subscriptions, or orders. For personal order status, use the dedicated order lookup.'
  }
};

const SUGGESTED_QUESTIONS = [
  'Which product is best for dry or sensitive skin?',
  'What do hyaluronic acid and ceramides do in HydraCalm Moisturizer?',
  'Where is my order?'
];

const FALLBACK_PATTERN = /i don['’]?t see that in the documents yet|i couldn['’]?t find (?:that|an answer|a reliable answer)|no (?:relevant )?documents found|couldn['’]?t find a reliable answer/i;
const PERSONAL_ORDER_PATTERN = /\b(?:where(?:'s| is) my order|where is order\s*(?:#|number)?\s*[\w-]+|track (?:my )?order|tracking (?:my )?order|my order status|status of my order|has my order shipped|order status for (?:order\s*)?(?:#|number)?\s*[\w-]+)\b/i;

function safeHttpUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** Repairs common escaped Markdown and duplicated/nested links returned by the demo model. */
function normalizeMarkdown(value) {
  return String(value || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\\?\[([^\]]+)\]\\?\(\[(https?:\/\/[^\]\s]+)\]\((https?:\/\/[^)\s]+)\)\)?/g, (_match, label, firstUrl, secondUrl) => {
      const url = safeHttpUrl(secondUrl) || safeHttpUrl(firstUrl);
      return url ? `[${label}](${url})` : label;
    })
    .replace(/\\([*_`])/g, '$1');
}

function renderInlineMarkdown(value, keyPrefix) {
  const text = String(value || '');
  const tokenPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*(.+?)\*\*|__(.+?)__|\*(.+?)\*/g;
  const nodes = [];
  let lastIndex = 0;
  let match;
  let tokenIndex = 0;

  while ((match = tokenPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[1] !== undefined) {
      const href = safeHttpUrl(match[2]);
      nodes.push(
        href ? (
          <a
            key={`${keyPrefix}-link-${tokenIndex}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-700 underline underline-offset-2 break-words"
          >
            {match[1]}
          </a>
        ) : match[1]
      );
    } else if (match[3] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-bold-${tokenIndex}`}>{match[3]}</strong>);
    } else if (match[4] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-bold-${tokenIndex}`}>{match[4]}</strong>);
    } else if (match[5] !== undefined) {
      nodes.push(<em key={`${keyPrefix}-italic-${tokenIndex}`}>{match[5]}</em>);
    }

    lastIndex = tokenPattern.lastIndex;
    tokenIndex += 1;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

/** A lightweight, dependency-free renderer for paragraphs, lists, bold text, italics, and safe links. */
function FormattedAnswer({ text }) {
  const lines = normalizeMarkdown(text).split('\n');
  const blocks = [];
  let paragraphLines = [];
  let currentList = null;

  function flushParagraph() {
    if (paragraphLines.length) {
      blocks.push({ type: 'paragraph', text: paragraphLines.join(' ').trim() });
      paragraphLines = [];
    }
  }

  function flushList() {
    if (currentList) {
      blocks.push(currentList);
      currentList = null;
    }
  }

  lines.forEach((line) => {
    if (!line.trim()) {
      flushParagraph();
      flushList();
      return;
    }

    const bulletMatch = line.match(/^\s*[-*•]\s+(.+)$/);
    const numberedMatch = line.match(/^\s*\d+[.)]\s+(.+)$/);
    const listType = numberedMatch ? 'ordered-list' : bulletMatch ? 'unordered-list' : null;

    if (listType) {
      flushParagraph();
      if (currentList && currentList.type !== listType) flushList();
      if (!currentList) currentList = { type: listType, items: [] };
      currentList.items.push((numberedMatch || bulletMatch)[1]);
    } else {
      flushList();
      paragraphLines.push(line.trim());
    }
  });

  flushParagraph();
  flushList();

  if (!blocks.length) return null;

  return (
    <div className="space-y-2 leading-relaxed">
      {blocks.map((block, index) => {
        if (block.type === 'paragraph') {
          return (
            <p key={`p-${index}`}>
              {renderInlineMarkdown(block.text, `p-${index}`)}
            </p>
          );
        }

        const ListTag = block.type === 'ordered-list' ? 'ol' : 'ul';
        const listClass = block.type === 'ordered-list'
          ? 'list-decimal pl-5 space-y-1'
          : 'list-disc pl-5 space-y-1';

        return (
          <ListTag key={`list-${index}`} className={listClass}>
            {block.items.map((item, itemIndex) => (
              <li key={`list-${index}-item-${itemIndex}`}>
                {renderInlineMarkdown(item, `list-${index}-item-${itemIndex}`)}
              </li>
            ))}
          </ListTag>
        );
      })}
    </div>
  );
}

function getHelpfulAnswer(rawAnswer, sources) {
  const answer = String(rawAnswer || '').trim();
  if (!answer || FALLBACK_PATTERN.test(answer)) {
    return sources.length
      ? 'I couldn’t find a reliable answer in the available content. The related sources below may help.'
      : 'I couldn’t find a reliable answer in the available content. Try a more specific question or one of the suggested prompts above.';
  }
  return answer;
}

function getSourceInfo(source, index) {
  if (typeof source === 'string') {
    return { label: source, url: safeHttpUrl(source) };
  }

  const url = safeHttpUrl(source?.url || source?.href || source?.sourceUrl || source?.source_url);
  const label = source?.title || source?.name || source?.label || url || `Source ${index + 1}`;
  return { label: String(label), url };
}

function orderStatusMessage(order) {
  const orderNumber = order?.order_number || order?.name || order?.orderNumber;
  const status = order?.fulfillment_status || order?.fulfillmentStatus || order?.status;
  const details = [];

  if (orderNumber) details.push(`Order ${orderNumber}`);
  if (status) details.push(`fulfillment status: ${status}`);

  if (details.length) return `${details.join(' — ')}.`;
  return 'The order lookup found a matching order. Check the lookup result for the details available in this demo.';
}

export default function RagDemo() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [queryCount, setQueryCount] = useState(0);
  const [escalationPreviewFor, setEscalationPreviewFor] = useState(null);
  const orderLookupRef = useRef(null);

  const current = DEMO_MODES.dtc_supplements;

  useEffect(() => {
    setMessages([
      {
        id: 'sys',
        who: 'system',
        text: current.systemText
      }
    ]);
  }, [current.systemText]);

  function scrollToOrderLookup() {
    if (orderLookupRef.current) {
      orderLookupRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  async function sendQuery(suggestedQuestion) {
    const text = String(suggestedQuestion ?? input).trim();
    if (!text || loading) return;

    setMessages((currentMessages) => [
      ...currentMessages,
      { id: `${Date.now()}-user`, who: 'you', text }
    ]);
    setQueryCount((count) => count + 1);
    setEscalationPreviewFor(null);
    setInput('');

    // Personal order-status requests use the dedicated lookup instead of retrieval over general documents.
    if (PERSONAL_ORDER_PATTERN.test(text)) {
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: `${Date.now()}-order-guidance`,
          who: 'bot',
          text: 'For personal order status, use the order lookup form above rather than general product search. Try #1001 or #DEMO123 if those sample orders are configured in this demo.',
          showEscalate: true,
          handoffQuestion: text
        }
      ]);
      scrollToOrderLookup();
      return;
    }

    setLoading(true);
    try {
      if (!API_ROOT) throw new Error('API_BASE is not configured');

      const res = await axios.post(`${API_ROOT}/rag/query`, {
        query: text,
        tenantId: current.tenantId,
        assistantType: current.assistantType,
        namespace: current.namespace
      });

      const rawAnswer = res.data?.answer || '';
      const sources = Array.isArray(res.data?.sources) ? res.data.sources : [];
      const answer = getHelpfulAnswer(rawAnswer, sources);

      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: `${Date.now()}-bot`,
          who: 'bot',
          text: answer,
          sources,
          showEscalate: true,
          handoffQuestion: text
        }
      ]);
    } catch (error) {
      // Keep the public demo response helpful without exposing request details.
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: `${Date.now()}-error`,
          who: 'bot',
          text: 'The demo assistant couldn’t be reached just now. Please try again in a moment.',
          showEscalate: true,
          handoffQuestion: text
        }
      ]);
    } finally {
      setLoading(false);
    }
  }

  function toggleEscalationPreview(messageId) {
    setEscalationPreviewFor((currentId) => (currentId === messageId ? null : messageId));
  }

  function onOrderResult(order) {
    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: `${Date.now()}-order-result`,
        who: 'bot',
        text: orderStatusMessage(order),
        system: true,
        showEscalate: true,
        handoffQuestion: `Order lookup${order?.order_number || order?.name ? ` for ${order.order_number || order.name}` : ''}`
      }
    ]);
  }

  function onOrderNoMatch() {
    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: `${Date.now()}-order-no-match`,
        who: 'bot',
        text: 'No matching order was returned. Try #1001 or #DEMO123 if those sample records are configured, or use an order number or email that exists in the connected demo data.',
        system: true,
        showEscalate: true,
        handoffQuestion: 'Order lookup returned no match'
      }
    ]);
  }

  const showMidCTA = queryCount >= 2;

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* HERO */}
      <header className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-full px-4 py-2 text-xs sm:text-sm text-indigo-800 mb-4">
          <span className="font-semibold">Live product demo</span>
          <span>grounded answers • product discovery • order lookup • handoff preview</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
          See a product-aware AI search + support experience in action.
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
          Ask real customer questions and see how a grounded assistant can help shoppers find products,
          understand product information, and find the right path for common support questions.
        </p>
      </header>

      {/* WHAT TO TEST */}
      <section className="mb-5 border rounded-xl p-5 bg-white shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
          <div>
            <h2 className="font-semibold text-lg">Try the three moments that matter</h2>
            <p className="text-sm text-gray-600 mt-1">
              Product discovery, product knowledge, and order support — in one experience.
            </p>
          </div>
          <div className="text-xs text-gray-500">Demo mode: {current.label}</div>
        </div>

        <div className="flex flex-wrap gap-2">
          {SUGGESTED_QUESTIONS.map((question) => (
            <button
              key={question}
              type="button"
              onClick={() => sendQuery(question)}
              disabled={loading}
              className="px-3 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {question}
            </button>
          ))}
        </div>
      </section>

      {/* ORDER LOOKUP */}
      <section
        ref={orderLookupRef}
        id="demo-order-lookup"
        className="bg-white border rounded-xl p-5 mb-5 shadow-sm scroll-mt-6"
        aria-labelledby="demo-order-lookup-title"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h2 id="demo-order-lookup-title" className="font-semibold text-sm">Try order lookup</h2>
            <p className="text-xs text-gray-600 mt-1">
              Personal order status belongs in the dedicated lookup flow, not general product-document search.
            </p>
          </div>
          <span className="text-xs text-gray-500 whitespace-nowrap">Optional workflow</span>
        </div>
        <OrderLookup onResult={onOrderResult} onNoMatch={onOrderNoMatch} />
        <p className="text-xs text-gray-500 mt-2">
          Demo order numbers to try: <strong>#1001</strong> or <strong>#DEMO123</strong>. They return results only if the matching sample records exist in the connected demo data.
        </p>
      </section>

      {/* CHAT */}
      <section className="border rounded-xl p-5 mb-4 bg-white min-h-[300px] shadow-sm" aria-label="Customer assistant demo">
        <div className="flex items-center justify-between border-b pb-3 mb-4">
          <div>
            <div className="font-semibold text-sm">Customer assistant</div>
            <div className="text-xs text-gray-500">Built around the demo catalog and support content</div>
          </div>
          <div className="text-xs text-gray-500">Grounded demo</div>
        </div>

        {messages.map((message) => (
          <div key={message.id} className={`mb-3 ${message.who === 'you' ? 'text-right' : ''}`}>
            <div
              className={`inline-block max-w-[90%] px-3 py-2 rounded-lg text-sm text-left ${
                message.who === 'you' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'
              }`}
            >
              <FormattedAnswer text={message.text} />

              {message.sources?.length > 0 && (
                <div className="mt-3 pt-2 border-t border-gray-200 text-left">
                  <div className="text-[11px] font-semibold text-gray-500 mb-1">Related sources</div>
                  <ul className="space-y-1">
                    {message.sources.slice(0, 4).map((source, index) => {
                      const info = getSourceInfo(source, index);
                      return (
                        <li key={`${info.label}-${index}`} className="text-[11px] text-gray-600 break-words">
                          {info.url ? (
                            <a
                              href={info.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-700 underline underline-offset-2"
                            >
                              {info.label}
                            </a>
                          ) : info.label}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {message.who === 'bot' && message.showEscalate && (
                <div className="mt-3 pt-2 border-t border-gray-200 text-left">
                  <button
                    type="button"
                    onClick={() => toggleEscalationPreview(message.id)}
                    aria-expanded={escalationPreviewFor === message.id}
                    className="text-xs font-medium text-indigo-700 underline underline-offset-2 hover:text-indigo-900"
                  >
                    {escalationPreviewFor === message.id ? 'Hide support handoff preview' : 'Escalate to support'}
                  </button>

                  {escalationPreviewFor === message.id && (
                    <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-gray-700" role="status">
                      <div className="font-semibold text-indigo-900">Support handoff preview</div>
                      <p className="mt-1">
                        In a client deployment, this can pass the customer’s question and relevant conversation context to the store’s designated support inbox or helpdesk.
                      </p>
                      {message.handoffQuestion && (
                        <p className="mt-2 break-words">
                          <span className="font-semibold">Question/context:</span> {message.handoffQuestion}
                        </p>
                      )}
                      <p className="mt-2 text-gray-600">
                        Demo notice: this public demo is not connected to a live support queue, so no support request has been sent.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="mb-3">
            <div className="inline-block px-3 py-2 rounded-lg bg-gray-100 text-gray-500 text-sm" role="status" aria-live="polite">
              Checking the available content…
            </div>
          </div>
        )}
      </section>

      {/* INPUT */}
      <form
        className="flex gap-2 mb-6"
        onSubmit={(event) => {
          event.preventDefault();
          sendQuery();
        }}
      >
        <input
          className="border rounded-lg px-3 py-2 flex-grow min-w-0"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Ask about a product, ingredient, or policy…"
          aria-label="Ask the customer assistant a question"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50"
        >
          {loading ? 'Checking…' : 'Ask'}
        </button>
      </form>

      {/* DIFFERENTIATION */}
      <section className="mb-6 grid gap-4 md:grid-cols-3 text-sm">
        <div className="border rounded-xl p-4 bg-gray-50">
          <h3 className="font-semibold mb-1">Grounded in your content</h3>
          <p className="text-gray-600">Use your catalog, FAQs, policies, and educational material rather than generic answers.</p>
        </div>
        <div className="border rounded-xl p-4 bg-gray-50">
          <h3 className="font-semibold mb-1">Product-aware</h3>
          <p className="text-gray-600">Help shoppers compare, understand, and choose products while they are deciding.</p>
        </div>
        <div className="border rounded-xl p-4 bg-gray-50">
          <h3 className="font-semibold mb-1">Configurable human handoff</h3>
          <p className="text-gray-600">Give unresolved questions a path to a store’s support team. The destination and handling can be configured for each client deployment.</p>
        </div>
      </section>
      <p className="-mt-3 mb-6 text-xs text-gray-500 text-center">
        Order status uses a dedicated lookup flow; support handoff above is shown as a preview in this public demo.
      </p>

      {/* MID DEMO CTA */}
      {showMidCTA && (
        <div className="mb-6 bg-indigo-50 border border-indigo-200 rounded-xl p-5 text-center">
          <p className="text-sm text-gray-700 mb-1 font-medium">
            This is the experience your customers could have on your site.
          </p>
          <p className="text-xs text-gray-600 mb-3">
            We can prepare a private version using your own product and support content before you install anything.
          </p>
          <a
            href="/trial"
            className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-lg"
          >
            Start your 7-day trial
          </a>
        </div>
      )}

      {/* FINAL CTA */}
      <div className="text-center border-t pt-6">
        <p className="text-sm text-gray-700 mb-2 font-medium">
          See it with your own content before you commit to an installation.
        </p>
        <p className="text-xs text-gray-500 mb-4">
          Private preview • usually ~48 hours • self-serve or done-for-you install
        </p>

        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <a href="/trial" className="px-5 py-2 bg-indigo-600 text-white rounded-lg">
            Start 7-day trial
          </a>
          <a href="/" className="px-5 py-2 border rounded-lg">
            Back to homepage
          </a>
        </div>
      </div>
    </div>
  );
}
